import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaCalendarAlt, FaSpinner, FaGift, FaStar } from 'react-icons/fa';
import { apiConnector } from '../../../Services/ApiConnector';
import { astrologyEndPoint } from '../../../Services/AllApi';
import { useLanguage } from '../../../i18n/LanguageContext';
import toast from 'react-hot-toast';

const FestivalsSection = () => {
  const { t } = useLanguage();
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [currentDate, setCurrentDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const fetchFestivals = async (year, month) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiConnector('GET', `${astrologyEndPoint.FESTIVALS_API}?year=${year}&month=${month}`, null, null);

      console.log('Festivals API Response:', response.data);
      
      if (response.data && response.data.data) {
        console.log('Setting festivals:', response.data.data);
        setFestivals(response.data.data);
      } else {
        console.log('No festivals data found, setting empty array');
        setFestivals([]);
      }
    } catch (err) {
      console.error('Error fetching festivals:', err);
      setError('Failed to fetch festivals data');
      toast.error('Failed to load festivals');
      setFestivals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFestivals(currentYear, currentMonth);
  }, [currentYear, currentMonth]);

  const handlePreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const isToday = (festivalDate) => {
    const today = new Date();
    const festival = new Date(festivalDate);
    return today.getDate() === festival.getDate() && 
           today.getMonth() === festival.getMonth() && 
           today.getFullYear() === festival.getFullYear();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long'
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const todayVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 10
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-orange-500 mx-auto mb-4" />
            <p className="text-gray-600">{t('loadingFestivals')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center py-12">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('errorLoadingFestivals')}</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchFestivals(currentYear, currentMonth)}
            className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            {t('tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header with Month Navigation */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <FaCalendarAlt className="text-orange-500 text-2xl" />
          <h2 className="text-2xl font-bold text-gray-900">
            {t('festivals')} - {monthNames[currentMonth - 1]} {currentYear}
          </h2>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handlePreviousMonth}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
            title="Previous Month"
          >
            <FaChevronLeft className="text-gray-600" />
          </button>
          
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
            title="Next Month"
          >
            <FaChevronRight className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Festivals List */}
      {console.log('Current festivals state:', festivals)}
      {festivals.length === 0 ? (
        <div className="text-center py-12">
          <FaGift className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('noFestivalsThisMonth')}</h3>
          <p className="text-gray-600">{t('noFestivalsScheduled')} {monthNames[currentMonth - 1]} {currentYear}</p>
        </div>
      ) : (
        <motion.div
          className="space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <AnimatePresence mode="wait">
            {festivals.map((festival, index) => {
              const isTodayFestival = isToday(festival.date);
              
              return (
                <motion.div
                  key={`${festival.date}-${index}`}
                  variants={isTodayFestival ? todayVariants : itemVariants}
                  className={`relative p-6 rounded-lg border-l-4 transition-all duration-300 ${
                    isTodayFestival 
                      ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-500 shadow-lg ring-2 ring-orange-200' 
                      : 'bg-gray-50 border-gray-300 hover:bg-gray-100 hover:shadow-md'
                  }`}
                  whileHover={{ 
                    scale: 1.02,
                    transition: { type: "spring", stiffness: 300, damping: 20 }
                  }}
                >
                  {/* Today Badge */}
                  {isTodayFestival && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 200, damping: 10 }}
                      className="absolute -top-2 -right-2 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1"
                    >
                      <FaStar className="text-xs" />
                      {t('today')}
                    </motion.div>
                  )}

                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-3 h-3 rounded-full ${isTodayFestival ? 'bg-orange-500' : 'bg-gray-400'}`}></div>
                        <h3 className={`text-lg font-semibold ${isTodayFestival ? 'text-orange-700' : 'text-gray-900'}`}>
                          {festival.name}
                        </h3>
                      </div>
                      
                      <div className="ml-6 space-y-2">
                        <div className="flex items-center gap-2 text-gray-600">
                          <span className="font-medium">Date:</span>
                          <span>{formatDate(festival.date)}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-sm">{formatDay(festival.date)}</span>
                        </div>
                        
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
};

export default FestivalsSection;
